import React from "react";

/**
 * Models often glue "(B) … (C) …" on one line. Break before each lettered "(X) "
 * so line-based parsing can turn them into a list. Long dash rules become paragraph gaps.
 */
function normalizeInlineLetterMarkers(text) {
  let s = String(text || "");
  s = s.replace(/([^\n])\s*\(([A-Za-z])\)\s+/g, "$1\n($2) ");
  s = s.replace(/\s-{5,}\s*/g, "\n\n");
  return s;
}

/**
 * Detect a single line as a list row: (A) …, (1) …, 1. …, - …
 */
function parseListLine(line) {
  const t = String(line || "").trim();
  if (!t) return null;
  const letter = /^\(([A-Za-z])\)\s*(.*)$/.exec(t);
  if (letter) return { variant: "ol-alpha", body: letter[2].trim() || letter[1] };
  const parenNum = /^\((\d+)\)\s*(.*)$/.exec(t);
  if (parenNum) return { variant: "ol", body: parenNum[2].trim() || parenNum[1] };
  const dotNum = /^(\d+)\.\s+(.*)$/.exec(t);
  if (dotNum) return { variant: "ol", body: dotNum[2].trim() };
  const bullet = /^[-*•]\s+(.*)$/.exec(t);
  if (bullet) return { variant: "ul", body: bullet[1].trim() };
  return null;
}

function flushPlain(buffer, segments) {
  const text = buffer.join("\n").trim();
  if (text) segments.push({ kind: "plain", text });
  buffer.length = 0;
}

function flushList(items, variant, segments) {
  if (items.length && variant) {
    segments.push({ kind: "list", variant, items: [...items] });
  }
  items.length = 0;
}

/**
 * Split body text into alternating plain paragraphs and detected lists.
 */
function parseSegments(raw) {
  const text = normalizeInlineLetterMarkers(raw);
  if (!text.trim()) return [];

  const lines = text.split("\n");
  const segments = [];
  const plainBuffer = [];
  const listItems = [];
  let listVariant = null;

  const endList = () => {
    flushList(listItems, listVariant, segments);
    listVariant = null;
  };

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();
    if (!trimmed) {
      endList();
      if (plainBuffer.length) plainBuffer.push("");
      continue;
    }

    const parsed = parseListLine(trimmed);
    if (!parsed) {
      endList();
      plainBuffer.push(trimmed);
      continue;
    }

    flushPlain(plainBuffer, segments);
    if (listItems.length && listVariant && listVariant !== parsed.variant) {
      flushList(listItems, listVariant, segments);
      listVariant = null;
    }
    if (!listVariant) listVariant = parsed.variant;
    listItems.push(parsed.body);
  }

  endList();
  flushPlain(plainBuffer, segments);

  return segments;
}

/**
 * Renders reflection / evidence strings: real <ul>/<ol> when lines look like a list.
 */
const ReflectionRichText = ({ text, className = "" }) => {
  const segments = parseSegments(text);
  if (segments.length === 0) return null;

  return (
    <div className={`reflection-rich-text ${className}`.trim()}>
      {segments.map((seg, idx) => {
        if (seg.kind === "plain") {
          return (
            <p key={idx} className="reflection-rich-text__plain">
              {seg.text}
            </p>
          );
        }
        if (seg.variant === "ul") {
          return (
            <ul key={idx} className="reflection-rich-text__list reflection-rich-text__list--bullets">
              {seg.items.map((item, j) => (
                <li key={j}>{item}</li>
              ))}
            </ul>
          );
        }
        if (seg.variant === "ol-alpha") {
          return (
            <ol key={idx} type="A" className="reflection-rich-text__list reflection-rich-text__list--alpha">
              {seg.items.map((item, j) => (
                <li key={j}>{item}</li>
              ))}
            </ol>
          );
        }
        return (
          <ol key={idx} className="reflection-rich-text__list reflection-rich-text__list--numbered">
            {seg.items.map((item, j) => (
              <li key={j}>{item}</li>
            ))}
          </ol>
        );
      })}
    </div>
  );
};

export default ReflectionRichText;
